/* global React, Appear, Counter, useInView, CaseBreadcrumb, FooterCaseCondensed, LangToggle */
const { useState, useEffect, useRef } = React;

// ─── DJY Pro case study reviews (panel lateral) ────────────────────────────
// text/role como { es, en } para poder cambiar idioma sin perder fidelidad.
const DJY_REVIEWS = [
  {
    text: {
      es: "¡Muchísimas gracias por todas tus enormes contribuciones al lanzamiento de DJYPro! Te vamos a extrañar mucho — fue un genuino placer colaborar con vos.",
      en: "Thank you so much for all your immense contributions to the launch of DJYPro! You will be greatly missed — it was a genuine pleasure collaborating with you.",
    },
    name: "Jawahar Desai",
    role: {
      es: "Director of Product Management, B2B Products · Dow Jones",
      en: "Director of Product Management, B2B Products · Dow Jones",
    },
  },
  {
    text: {
      es: "Tu aporte fue invaluable. Una verdadera jugadora de equipo, y alguien siempre dispuesta a encontrar puntos en común.",
      en: "Your input was invaluable. A true team player, and someone always willing to find common ground.",
    },
    name: "Tim White",
    role: {
      es: "Senior Product Director · Dow Jones",
      en: "Senior Product Director · Dow Jones",
    },
  },
];

// Helper para extraer string de un campo que puede ser plano o { es, en }.
function djyL(v, lang) {
  if (v && typeof v === "object" && (v.es || v.en)) return v[lang] || v.es || v.en;
  return v;
}

// ─── Layer pipeline diagram (4.5 metodología) ─────────────────────────────
// Three layered cards on the left feed into a DEV card on the right.
// On desktop: each capa has its own dashed feed line meeting a shared vertical
//   bus, which then sends a single dashed arrow into DEV.
// On mobile: cards stack full-width with ↓ arrows between them (no bus).
// The bus + arrow positions are computed from real DOM measurements, so the
// diagram stays aligned across viewport sizes.
function CaseLayersDiagram({ lang = "es" }) {
  const t = (es, en) => (lang === "en" ? en : es);
  const wrapRef = useRef(null);
  const stackRef = useRef(null);
  const card01Ref = useRef(null);
  const card03Ref = useRef(null);
  const devRef = useRef(null);
  const busRef = useRef(null);
  const bridgeRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const wrap = wrapRef.current;
    const stack = stackRef.current;
    const card01 = card01Ref.current;
    const card03 = card03Ref.current;
    const dev = devRef.current;
    const bus = busRef.current;
    const bridge = bridgeRef.current;
    if (!wrap || !stack || !card01 || !card03 || !dev || !bus) return;

    function update() {
      const mobile = window.innerWidth < 900;
      setIsMobile(mobile);
      if (mobile) {
        bus.style.cssText = "";
        return;
      }
      const stackBox = stack.getBoundingClientRect();
      const c01Box = card01.getBoundingClientRect();
      const c03Box = card03.getBoundingClientRect();

      // Bus: extends from CAPA 01 centerline down to CAPA 03 centerline.
      const busTop = (c01Box.top + c01Box.height / 2) - stackBox.top;
      const busBottom = stackBox.bottom - (c03Box.top + c03Box.height / 2);
      bus.style.top = busTop + "px";
      bus.style.bottom = busBottom + "px";
    }

    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div
      className={`case-layers ${isMobile ? "is-mobile" : ""}`}
      ref={wrapRef}
      role="group"
      aria-label={t(
        "Pipeline semanal: tres capas de diseño que alimentan al equipo de desarrollo",
        "Weekly pipeline: three design layers feeding into the development team"
      )}
    >
      <div className="case-layers-stack" ref={stackRef}>
        <div className="case-layer-row case-layer-row--01">
          <article className="case-layer case-layer--01" ref={card01Ref}>
            <div className="case-layer-head mono">
              <span className="case-layer-tag">{t("CAPA 01", "LAYER 01")}</span>
              <span className="case-layer-role">UXA</span>
            </div>
            <h3 className="case-layer-title mono">{t("Estructura y Comportamiento", "Structure & Behavior")}</h3>
            <p className="case-layer-body">{t("Flujos y comportamientos en baja fidelidad.", "Flows and behaviors in low fidelity.")}</p>
          </article>
          <span className="case-layer-feed" aria-hidden="true"></span>
        </div>
        <span className="case-layer-step" aria-hidden="true">↓</span>
        <div className="case-layer-row case-layer-row--02">
          <article className="case-layer case-layer--02">
            <div className="case-layer-head mono">
              <span className="case-layer-tag">{t("CAPA 02", "LAYER 02")}</span>
              <span className="case-layer-role">UXD</span>
            </div>
            <h3 className="case-layer-title mono">{t("Diseño Visual y Estilos", "Visual Design & Styles")}</h3>
            <p className="case-layer-body">{t("Iconos, estilos y espaciados sobre la estructura.", "Icons, styles and spacing on top of the structure.")}</p>
          </article>
          <span className="case-layer-feed" aria-hidden="true"></span>
        </div>
        <span className="case-layer-step" aria-hidden="true">↓</span>
        <div className="case-layer-row case-layer-row--03">
          <article className="case-layer case-layer--03" ref={card03Ref}>
            <div className="case-layer-head mono">
              <span className="case-layer-tag">{t("CAPA 03", "LAYER 03")}</span>
              <span className="case-layer-role">DS</span>
            </div>
            <h3 className="case-layer-title mono">{t("Sistema de Componentes", "Component System")}</h3>
            <p className="case-layer-body">{t("Componentes, tokens y documentación de A11y.", "Components, tokens and a11y documentation.")}</p>
          </article>
          <span className="case-layer-feed" aria-hidden="true"></span>
        </div>
        <span className="case-layers-bus" ref={busRef} aria-hidden="true"></span>
      </div>

      <div className="case-layers-bridge" ref={bridgeRef} aria-hidden="true">
        <span className="case-layers-bridge-line"></span>
        <span className="case-layers-bridge-arrow">→</span>
      </div>

      <span className="case-layer-step case-layer-step--final" aria-hidden="true">↓</span>

      <article className="case-layer case-layer--dev" ref={devRef}>
        <div className="case-layer-head mono">
          <span className="case-layer-role">DEV</span>
        </div>
        <p className="case-layer-body">
          {t(
            "Handoff parcial. Avanza en paralelo, recibiendo cada capa apenas está lista.",
            "Incremental handoff. Moves in parallel, picking up each layer as soon as it's ready."
          )}
        </p>
      </article>
    </div>
  );
}

// ─── Responsive Resize animation ──────────────────────────────────────────
// Frame width animates 1200 ↔ 360 px in an 8s loop. Two stacked images
// (desktop + mobile) crossfade based on the current width.
// Frame height interpolates between each image's natural aspect ratio so no
// image is ever cut off. Respects prefers-reduced-motion.
function ResponsiveResize({ lang = "es" }) {
  const t = (es, en) => (lang === "en" ? en : es);
  const stageRef = useRef(null);
  const frameRef = useRef(null);
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    const frame = frameRef.current;
    const desktop = desktopRef.current;
    const mobile = mobileRef.current;
    const label = labelRef.current;
    if (!stage || !frame || !desktop || !mobile) return;

    const reduced = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Intrinsic image dimensions
    const D_W = 2880, D_H = 1986;
    const M_W = 354,  M_H = 766;
    const D_RATIO = D_H / D_W;          // 0.69
    const M_RATIO = M_H / M_W;          // 2.16

    // Animation params — conceptual viewport widths. The frame is rendered
    // at `w * scale` where scale = min(widthFit, heightFit), so the *label*
    // always reads 360..1200 (matching the caption) while the *visual*
    // frame fits whatever room the column has AND a 600px tall stage cap
    // (matching the DJY Pro image-cap pattern). Same round trip on every
    // device — nothing clips, nothing breaks at any width.
    const W_MAX = 1200;
    const W_MIN = 360;
    const XF_HIGH = 760;   // desktop image dominant above this
    const XF_LOW = 480;    // mobile image dominant below this
    const CYCLE_MS = 9000;
    const HOLD = 0.16;
    const TRANS = (1 - 2 * HOLD) / 2;
    const STAGE_PAD = 40;  // vertical breathing room around the frame
    const STAGE_INSET = 32; // matches CSS horizontal padding
    // Stage height cap — matches the 600px / 70vh used for case images
    // elsewhere in DJY Pro. Computed inside getScale() so it tracks
    // viewport resizes (otherwise the cap freezes at mount-time height).
    function stageMaxH() {
      return Math.min(600, Math.round(window.innerHeight * 0.7));
    }

    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    function widthAt(t) {
      let frac;
      if (t < HOLD)                       frac = 0;
      else if (t < HOLD + TRANS)          frac = easeInOutCubic((t - HOLD) / TRANS);
      else if (t < HOLD + TRANS + HOLD)   frac = 1;
      else                                frac = 1 - easeInOutCubic((t - HOLD * 2 - TRANS) / TRANS);
      return W_MAX - frac * (W_MAX - W_MIN);
    }
    function heightAt(w) {
      const t = (w - XF_LOW) / (XF_HIGH - XF_LOW);
      const dOp = Math.max(0, Math.min(1, t));
      const mOp = 1 - dOp;
      return { h: w * (D_RATIO * dOp + M_RATIO * mOp), dOp, mOp };
    }
    function peakConceptualHeight() {
      let maxH = 0;
      for (let w = W_MIN; w <= W_MAX; w += 8) {
        const { h } = heightAt(w);
        if (h > maxH) maxH = h;
      }
      return Math.max(maxH, heightAt(W_MAX).h, heightAt(W_MIN).h);
    }
    function getScale() {
      // Scale frame to fit the stage. Constrained by BOTH the available
      // width (column may be narrower than 1200) AND the stage height cap
      // (so the section never grows taller than ~600px). The tighter of
      // the two constraints wins — the frame always fits without clipping
      // on any viewport.
      const availW = Math.max(1, stage.clientWidth - STAGE_INSET);
      const availH = Math.max(1, stageMaxH() - STAGE_PAD);
      const sW = availW / W_MAX;
      const sH = availH / peakConceptualHeight();
      return Math.min(1, sW, sH);
    }
    function apply(w) {
      const scale = getScale();
      frame.style.width = (w * scale) + "px";
      const { h, dOp, mOp } = heightAt(w);
      frame.style.height = (h * scale) + "px";
      desktop.style.opacity = String(dOp);
      mobile.style.opacity = String(mOp);
      if (label) label.textContent = Math.round(w) + " px";
    }

    // Pre-compute the peak height across the entire cycle so the stage can
    // be a fixed size — the frame animates *inside* it without pushing the
    // page content below.
    function computeStageHeight() {
      const scale = getScale();
      let maxH = 0;
      for (let w = W_MIN; w <= W_MAX; w += 8) {
        const { h } = heightAt(w);
        if (h > maxH) maxH = h;
      }
      // Also consider the exact endpoints
      maxH = Math.max(maxH, heightAt(W_MAX).h, heightAt(W_MIN).h);
      return Math.ceil(maxH * scale);
    }

    function setStageHeight() {
      stage.style.height = (computeStageHeight() + STAGE_PAD) + "px";
    }

    setStageHeight();

    if (reduced) {
      apply(W_MAX);
      return;
    }

    let raf;
    const start = performance.now();
    function tick(now) {
      const t = ((now - start) % CYCLE_MS) / CYCLE_MS;
      apply(widthAt(t));
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      setStageHeight();
      apply(widthAt(((performance.now() - start) % CYCLE_MS) / CYCLE_MS));
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <figure className="case-fig case-resize">
      <div className="case-resize-stage" ref={stageRef}>
        <div className="case-resize-frame" ref={frameRef}>
          <picture>
            <source srcSet="assets/cases/djy-pro/article-desktop.webp" type="image/webp" />
            <img
              ref={desktopRef}
              className="case-resize-desktop"
              src="assets/cases/djy-pro/article-desktop.png"
              alt={t(
                "Vista desktop del artículo individual: layout amplio con módulos lado a lado y datos visibles por defecto.",
                "Desktop view of the article: wide layout with side-by-side modules and data visible by default."
              )}
              width="2880"
              height="1986"
              loading="lazy"
              decoding="async"
            />
          </picture>
          <picture>
            <source srcSet="assets/cases/djy-pro/article-mobile.webp" type="image/webp" />
            <img
              ref={mobileRef}
              className="case-resize-mobile"
              src="assets/cases/djy-pro/article-mobile.png"
              alt={t(
                "Vista mobile del mismo artículo: layout reflowado a una columna, jerarquía adaptada y densidad de información ajustada al idioma activo.",
                "Mobile view of the same article: layout reflowed to a single column, hierarchy adapted and information density tuned to the active language."
              )}
              width="354"
              height="766"
              loading="lazy"
              decoding="async"
              style={{ opacity: 0 }}
            />
          </picture>
        </div>
      </div>
      <div className="case-resize-readout mono">
        <span className="case-resize-readout-label">viewport width</span>
        <span className="case-resize-readout-value" ref={labelRef}>1200 px</span>
      </div>
      <figcaption className="mono">
        {t(
          "Responsive resize · el mismo artículo de 1200 px a 360 px — el sistema adapta su comportamiento, no solo su visual",
          "Responsive resize · the same article from 1200px to 360px — the system adapts its behavior, not just its visual"
        )}
      </figcaption>
    </figure>
  );
}

// Case imagery: renders a real <picture> when `src` is provided, else a
// labeled placeholder rectangle. Used by CaseDJY and CaseDJYMore.
//   <CasePlaceholder id="PLACEHOLDER_X" caption="…" />              ← placeholder
//   <CasePlaceholder src="assets/.../foo" alt="…" caption="…"
//                    width={1600} height={983} ratio="1600 / 983" /> ← real image
function CasePlaceholder({
  id,
  src,
  alt,
  caption,
  ratio = "16 / 9",
  width,
  height,
  className = "",
}) {
  // srcset uses spaces as separators, so any unencoded space in the URL
  // splits the parse and silently drops the source (browser falls back
  // to <img src>). Encode once here so callers can pass human-readable
  // paths with spaces.
  const encodedSrc = src ? encodeURI(src) : src;
  return (
    <figure className={`case-fig ${className}`}>
      {src ? (
        <div className="case-img-wrap" style={{ aspectRatio: ratio }}>
          <picture>
            <source srcSet={`${encodedSrc}.webp`} type="image/webp" />
            <source srcSet={`${encodedSrc}.jpg`} type="image/jpeg" />
            <img
              src={`${encodedSrc}.png`}
              alt={alt || ""}
              width={width}
              height={height}
              loading="lazy"
              decoding="async"
            />
          </picture>
        </div>
      ) : (
        <div className="case-ph" style={{ aspectRatio: ratio }}>
          <span className="mono">{id}</span>
        </div>
      )}
      <figcaption className="mono">{caption}</figcaption>
    </figure>
  );
}

// ─── Case page ─────────────────────────────────────────────────────────────
function CaseDJY({ onBack, onOpenReviews, onOpenMore, reviewsOpened, lang = "es", setLang }) {
  const t = (es, en) => (lang === "en" ? en : es);
  const resultRef = useRef(null);

  // ── Auto-open desborde lateral al llegar a Resultado ──────────────────
  // Deshabilitado mientras el flow del desborde no se publica (mismo flag
  // que en el home). Recuperar el `useEffect` cuando se reactive.
  // useEffect(() => {
  //   if (reviewsOpened) return;
  //   const el = resultRef.current;
  //   if (!el) return;
  //   const io = new IntersectionObserver(
  //     ([e]) => {
  //       if (e.isIntersecting) {
  //         onOpenReviews && onOpenReviews();
  //         io.disconnect();
  //       }
  //     },
  //     { threshold: 0.4 }
  //   );
  //   io.observe(el);
  //   return () => io.disconnect();
  // }, [onOpenReviews, reviewsOpened]);

  return (
    <div className="case-page">
      <header className="process-nav">
        <div className="process-nav-inner shell">
          <CaseBreadcrumb onBackToProjects={onBack} lang={lang} />
          <span className="nav-brand">
            <span className="mark mono">anita<em>.</em>sevilla<em>.</em>ull</span>
          </span>
          <div className="process-nav-end">
            <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("caso · djy pro", "case · djy pro")}</span>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </header>

      {/* 1 — HERO */}
      <section className="case-hero shell">
        <div className="case-hero-grid">
          <div className="case-hero-text">
            <div className="tag tag-line">{t("CASO GIANTS para Dow Jones", "GIANTS CASE for Dow Jones")}</div>
            <h1 className="case-title mono">
              <span>{t(
                "Un producto nuevo, complejo, bilingüe, multicultural.",
                "A new product. Complex, bilingual, multicultural."
              )}</span>
            </h1>
            <p className="case-headline mono">
              <span className="case-stat">78%</span>
              <span>{t(" de retención de usuarios a 3 meses.", " user retention at 3 months.")}</span>
            </p>
            <p className="case-sub">
              {t(
                "Eso no pasa solo. Hay decisiones detrás, conócelas aquí.",
                "That doesn't happen on its own. There are decisions behind it — read on."
              )}
            </p>

            <p className="case-meta mono">
              <b>Dow Jones + Yomiuri</b>
              <span> · 2024 · </span>
              <mark className="case-meta-mark">UX Architect Lead</mark>
              <span>{t(" · Media & Finanzas", " · Media & Finance")}</span>
            </p>
          </div>

          <figure className="case-hero-media">
            <picture>
              <source srcSet="assets/cases/djy-pro/hero-giants.webp" type="image/webp" />
              <source srcSet="assets/cases/djy-pro/hero-giants.jpg" type="image/jpeg" />
              <img
                src="assets/cases/djy-pro/hero-giants.png"
                alt={t(
                  "Dashboard Giants — Dow Jones × Yomiuri — en tablet en perspectiva. Layout modular con paneles de noticias financieras y datos de mercado en español y japonés, con la marca Dow Jones al pie.",
                  "Giants dashboard — Dow Jones × Yomiuri — on a tablet in perspective. Modular layout with financial news panels and market data in Spanish and Japanese, Dow Jones brand at the foot."
                )}
                width="1536"
                height="1024"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                onError={(e) => { e.currentTarget.closest('.case-hero-media').classList.add('is-missing'); }}
              />
            </picture>
            <figcaption className="case-hero-fallback mono" aria-hidden="true">
              hero-giants.png · drop in /assets/cases/djy-pro
            </figcaption>
          </figure>
        </div>
      </section>

      {/* 2 — CONTEXTO */}
      <Appear className="case-section shell">
        <div className="section-head">
          <span className="tag">{t("// CONTEXTO", "// CONTEXT")}</span>
        </div>
        <div className="case-context-grid">
          <div className="case-prose">
            {lang === "en" ? (
              <p>
                Dow Jones and Yomiuri — the most-read newspaper in Japan — teamed
                up to build a product from zero: a{" "}
                <strong>modular dashboard for visualizing financial news, built
                for investors from two cultures</strong> with radically different
                ways of processing information.
              </p>
            ) : (
              <p>
                Dow Jones y Yomiuri — el diario más leído de Japón — se unieron
                para crear un producto desde cero: un{" "}
                <strong>dashboard modular de visualización de noticias
                financieras para inversores de dos culturas</strong> con formas
                radicalmente distintas de procesar la información.
              </p>
            )}
            {lang === "en" ? (
              <p>
                Internally we called the project <span className="case-hand">Giants</span>,
                after Yomiuri's baseball team and because it really was going to
                be giant. It had to work on desktop, mobile and TVs.{" "}
                <strong>
                  Users could have anywhere from 1 to 8 modules active at once,
                  each with its own content, filters and settings.
                </strong>
              </p>
            ) : (
              <p>
                El proyecto se llamó <span className="case-hand">Giants</span>{" "}
                internamente, por el equipo de béisbol de Yomiuri y porque el
                proyecto realmente sería gigante. Tenía que funcionar en desktop,
                mobile y televisores.{" "}
                <strong>
                  Los usuarios podían tener entre 1 y 8 módulos activos
                  simultáneamente, cada uno con su propio contenido, filtros y
                  configuración.
                </strong>
              </p>
            )}
          </div>
          <aside className="case-highlight" aria-label={t("Equipo", "Team")}>
            <span className="case-highlight-eyebrow tag">{t("En este proyecto", "On this project")}</span>
            {lang === "en" ? (
              <p>
                <strong>
                  I worked 6 months as UX Architect Lead on a 12-person team
                </strong>
                {" "}— 3 UXAs, 2 UXDs, 2 Design System Designers, 1 A11y Expert,
                1 PM, 1 PO and 1 Design Lead.
              </p>
            ) : (
              <p>
                <strong>
                  Trabajé 6 meses como UX Architect Lead en un equipo de 12
                  personas
                </strong>
                {" "}3 UXAs, 2 UXDs, 2 Design System Designers, 1 A11y Expert,
                1 PM, 1 PO y 1 Design Lead.
              </p>
            )}
          </aside>
        </div>
      </Appear>

      {/* 3 — EL PROBLEMA */}
      <Appear className="case-section shell">
        <div className="section-head">
          <span className="tag">{t("// EL PROBLEMA", "// THE PROBLEM")}</span>
        </div>
        <div className="case-prose">
          {lang === "en" ? (
            <React.Fragment>
              <p>
                We started with a blank page and a lot of pressure on it. No
                inherited flows, no system to extend. Just a brief, an internal
                Dow Jones reference and two cultures the team had to understand
                before designing a single screen.
              </p>
              <p>
                The most critical decision was also the earliest one:{" "}
                <strong>how was the modular system going to work?</strong> If we
                got that one wrong, everything built on top of it would be wrong
                too. There was no way to patch it later.
              </p>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <p>
                El punto de partida fue una hoja en blanco con mucha presión encima.
                No había flujos heredados, no había sistema previo que extender. Solo
                un brief, una referencia interna de Dow Jones y dos culturas que el
                equipo necesitaba entender antes de diseñar una sola pantalla.
              </p>
              <p>
                La decisión más crítica era también la más temprana:{" "}
                <strong>¿cómo iba a funcionar el sistema modular?</strong> Si esa
                decisión se tomaba mal, todo lo que se construyera después sobre ella
                estaría mal también. No había manera de parcharlo después.
              </p>
            </React.Fragment>
          )}
        </div>
      </Appear>

      {/* 4 — EL PROCESO */}
      <section className="case-section shell" id="case-proceso">
        <div className="section-head">
          <span className="tag">{t("// EL PROCESO", "// THE PROCESS")}</span>
        </div>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("4.1 · Entender antes de diseñar", "4.1 · Understand before designing")}</h2>
          <div className="case-prose">
            <p>
              {t(
                "El proceso comenzó con lectura de los BRDs y entrevistas al PO y a los stakeholders clave para entender qué necesitaba el producto, más allá de lo que pedían.",
                "The process started with reading the BRDs and interviewing the PO and the key stakeholders to understand what the product needed, beyond what they were asking for."
              )}
            </p>
          </div>
          {lang === "en" ? (
            <p className="case-feature">
              For <span className="case-hand">Western users</span>, less
              information on screen reduces cognitive load. For{" "}
              <span className="case-hand">Japanese users</span>, more visible
              context means less interpretive effort — information density
              builds confidence.
            </p>
          ) : (
            <p className="case-feature">
              Para los{" "}
              <span className="case-hand">usuarios occidentales</span>, menos
              información en pantalla reduce la carga cognitiva. Para los{" "}
              <span className="case-hand">usuarios japoneses</span>, más
              contexto visible equivale a menos esfuerzo de interpretación:
              la densidad de información genera confianza.
            </p>
          )}
          <div className="case-prose">
            <p>
              <strong>
                {t(
                  "Ese hallazgo obligó a replantear la lógica de progressive disclosure y la cantidad de información visible por defecto en los módulos.",
                  "That finding forced us to rethink the progressive disclosure logic and how much information was visible by default in each module."
                )}
              </strong>
            </p>
            <p>
              {t(
                "Después hicimos benchmark: revisamos el propio producto que había inspirado el encargo, otros dashboards modulares de finanzas, y productos con sistemas de bloques como Notion o Google Keep.",
                "Then we benchmarked: we reviewed the very product that had inspired the brief, other modular finance dashboards, and block-based products like Notion or Google Keep."
              )}
            </p>
          </div>
          <div className="case-benchmark-grid">
            <CasePlaceholder
              src="assets/cases/djy-pro/benchmark"
              alt={t(
                "Benchmark comparativo de productos con sistemas modulares — incluye dashboards financieros de referencia y productos basados en bloques como Notion y Google Keep.",
                "Comparative benchmark of products with modular systems — reference financial dashboards plus block-based products like Notion and Google Keep."
              )}
              width={3062}
              height={1966}
              ratio="3062 / 1966"
              caption={t("Benchmark de productos modulares de referencia", "Benchmark of reference modular products")}
            />
            <div className="case-benchmark-side">
              <h3 className="case-benchmark-title mono">
                {t("Dimensiones analizadas durante el benchmark", "Dimensions analyzed during the benchmark")}
              </h3>
              <ul className="case-bullets">
                <li>{t("Gestión de densidad de datos y carga cognitiva.", "Information density and cognitive load management.")}</li>
                <li>{t("Lógicas de reflujo inteligente vs. comportamiento estático en grillas.", "Smart reflow vs. static behavior in grids.")}</li>
                <li>{t("Consolidación de métricas en pantallas únicas.", "Consolidating metrics into single views.")}</li>
                <li>{t("Estandarización de herramientas interactivas y gráficos.", "Standardizing interactive tools and charts.")}</li>
              </ul>
            </div>
          </div>
          <div className="case-prose">
            <p>
              {t(
                "El análisis de competidores dejó dos problemas en evidencia: cómo manejar la densidad de información en el dashboard y qué pasa con los módulos cuando el idioma cambia y el texto se expande. El sistema tenía que aguantar eso sin romperse. ",
                "The competitor analysis surfaced two problems: how to manage information density in the dashboard, and what happens to modules when the language switches and the text expands. The system had to absorb that without breaking. "
              )}
              <strong>
                {t(
                  "La siguiente decisión fue definir las opciones de estructura del layout.",
                  "The next decision was to define the layout structure options."
                )}
              </strong>
            </p>
          </div>
        </Appear>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("4.2 · Las tres opciones de estructura", "4.2 · The three structure options")}</h2>
          <div className="case-prose">
            <p>
              {t(
                "Antes de prototipar cualquier interacción, presentamos al design lead tres alternativas de sistema modular — cada una con propuestas en baja fidelidad, reglas escritas y fundamentación clara:",
                "Before prototyping any interaction, we presented the design lead with three modular system alternatives — each one with low-fidelity proposals, written rules, and clear reasoning:"
              )}
            </p>
          </div>
          <ol className="case-options mono">
            <li>
              <div className="case-option-thumb">
                <picture>
                  <source
                    type="image/webp"
                    srcSet="assets/cases/djy-pro/bento-freeform.webp 1x, assets/cases/djy-pro/bento-freeform@2x.webp 2x"
                  />
                  <img
                    src="assets/cases/djy-pro/bento-freeform.png"
                    srcSet="assets/cases/djy-pro/bento-freeform.png 1x, assets/cases/djy-pro/bento-freeform@2x.png 2x"
                    alt={t(
                      "Diagrama del sistema Bento freeform — módulos de distintos tamaños distribuidos libremente sobre una grilla, sin columnas fijas.",
                      "Diagram of the Bento freeform system — modules of varying sizes distributed freely on a grid, no fixed columns."
                    )}
                    width="516"
                    height="240"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="case-option-body">
                <span className="case-n">01</span>
                <span><strong>Bento freeform</strong>{t(" — columnas libres, drag-and-drop sin restricciones", " — free columns, drag-and-drop with no constraints")}</span>
              </div>
            </li>
            <li>
              <div className="case-option-thumb">
                <picture>
                  <source
                    type="image/webp"
                    srcSet="assets/cases/djy-pro/bento-fixed-columns.webp 1x, assets/cases/djy-pro/bento-fixed-columns@2x.webp 2x"
                  />
                  <img
                    src="assets/cases/djy-pro/bento-fixed-columns.png"
                    srcSet="assets/cases/djy-pro/bento-fixed-columns.png 1x, assets/cases/djy-pro/bento-fixed-columns@2x.png 2x"
                    alt={t(
                      "Diagrama del sistema Bento fixed columns — cuatro columnas fijas con módulos de filas libres que apilan verticalmente.",
                      "Diagram of the Bento fixed columns system — four fixed columns with free-row modules stacking vertically."
                    )}
                    width="516"
                    height="239"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="case-option-body">
                <span className="case-n">02</span>
                <span><strong>Bento fixed columns</strong>{t(" — columnas fijas, filas libres", " — fixed columns, free rows")}</span>
              </div>
            </li>
            <li>
              <div className="case-option-thumb">
                <picture>
                  <source
                    type="image/webp"
                    srcSet="assets/cases/djy-pro/fixed-boxes-per-column.webp 1x, assets/cases/djy-pro/fixed-boxes-per-column@2x.webp 2x"
                  />
                  <img
                    src="assets/cases/djy-pro/fixed-boxes-per-column.png"
                    srcSet="assets/cases/djy-pro/fixed-boxes-per-column.png 1x, assets/cases/djy-pro/fixed-boxes-per-column@2x.png 2x"
                    alt={t(
                      "Diagrama del sistema Fixed 2 boxes per column — cuatro columnas fijas con hasta dos módulos cada una y alturas ajustables.",
                      "Diagram of the Fixed 2 boxes per column system — four fixed columns with up to two modules each and adjustable heights."
                    )}
                    width="516"
                    height="239"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="case-option-body">
                <span className="case-n">03</span>
                <span><strong>Fixed 2 boxes per column</strong>{t(" — 4 columnas fijas, hasta 2 módulos por columna, alturas ajustables", " — 4 fixed columns, up to 2 modules per column, adjustable heights")}</span>
              </div>
            </li>
          </ol>
          <div className="case-prose">
            <p>
              {t(
                "La más flexible era también la más difícil de predecir: el mismo usuario con 8 módulos en un monitor grande veía algo completamente distinto desde su teléfono. La inconsistencia hubiera roto la experiencia justo cuando más la necesitás.",
                "The most flexible one was also the hardest to predict: the same user with 8 modules on a big monitor would see something completely different on their phone. That inconsistency would have broken the experience right when you need it most."
              )}
            </p>
            <p>
              <strong>{t("La decisión fue módulos fijos con alturas estandarizadas", "The decision was fixed modules with standardized heights")}</strong>
              {t(
                " — no la más libre, sino la más predecible y escalable en todos los dispositivos.",
                " — not the freest, but the most predictable and scalable across devices."
              )}
            </p>
          </div>
        </Appear>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("4.3 · Probar el movimiento antes de diseñar el contenido", "4.3 · Test the motion before designing the content")}</h2>
          <div className="case-prose">
            <p>
              {t(
                "Definida la estructura, el siguiente desafío fue el comportamiento: ¿cómo se movían los módulos cuando el usuario los reordenaba?",
                "With the structure set, the next challenge was behavior: how should modules move when the user reorders them?"
              )}
            </p>
            <p>
              {t(
                "Prototipamos dos comportamientos en baja fidelidad visual — deliberadamente sin contenido real para que los usuarios evaluaran solo el movimiento:",
                "We prototyped two behaviors in low visual fidelity — deliberately without real content so users were judging the motion alone:"
              )}
            </p>
          </div>
          <figure className="case-fig case-fig--compact case-fig--bare">
            <div className="case-img-wrap" style={{ aspectRatio: "800 / 396" }}>
              <video
                src="assets/cases/djy-pro/comportamiento-modulos.webm"
                aria-label={t(
                  "Animación A/B comparando dos comportamientos de reordenamiento de módulos: a la izquierda swap estático (los módulos saltan a posiciones fijas), a la derecha reordenamiento fluido tipo magazine (los módulos se reacomodan en tiempo real mientras se arrastran).",
                  "A/B animation comparing two module reordering behaviors: on the left, static swap (modules jump to fixed positions); on the right, fluid magazine-style reflow (modules rearrange in real time while dragging)."
                )}
                width="800"
                height="396"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              />
            </div>
            <figcaption className="mono">
              {t(
                "Prototipo en baja fidelidad visual, focalizado solo en movimiento — la prueba A/B",
                "Low-fidelity prototype focused only on motion — the A/B test"
              )}
            </figcaption>
          </figure>

          <ol className="case-options mono case-options--ab">
            <li>
              <div className="case-option-thumb">
                <picture>
                  <source srcSet="assets/cases/djy-pro/static-behavior.webp" type="image/webp" />
                  <img
                    src="assets/cases/djy-pro/static-behavior.png"
                    alt={t(
                      "Resultado del test del comportamiento estático (swap): los módulos se desplazan a posiciones fijas. Resumen cuantitativo y cualitativo de la prueba con usuarios.",
                      "Result of the static behavior test (swap): modules shift to fixed positions. Quantitative and qualitative summary of the user test."
                    )}
                    width="740"
                    height="417"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="case-option-body">
                <span className="case-n">A</span>
                <span>
                  <strong>{t("swap estático", "static swap")}</strong>
                  {t(" — módulos que se desplazan a posiciones fijas", " — modules shift to fixed positions")}
                </span>
              </div>
            </li>
            <li>
              <div className="case-option-thumb">
                <picture>
                  <source srcSet="assets/cases/djy-pro/smart-reflow.webp" type="image/webp" />
                  <img
                    src="assets/cases/djy-pro/smart-reflow.png"
                    alt={t(
                      "Resultado del test del comportamiento fluido tipo magazine (smart reflow): los módulos se reacomodan en tiempo real mientras se arrastran. Resumen cuantitativo y cualitativo de la prueba con usuarios.",
                      "Result of the fluid magazine-style behavior test (smart reflow): modules rearrange in real time while dragging. Quantitative and qualitative summary of the user test."
                    )}
                    width="740"
                    height="417"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="case-option-body">
                <span className="case-n">B</span>
                <span>
                  <strong>{t("fluido tipo magazine", "fluid magazine-style")}</strong>
                  {t(" — módulos que se reacomodan en tiempo real mientras arrastrás", " — modules rearrange in real time while you drag")}
                </span>
              </div>
            </li>
          </ol>

          <div className="case-prose">
            <p>
              {t(
                "El movimiento fluido generó más satisfacción; el swap generó más sensación de control y confianza. ",
                "The fluid motion generated more satisfaction; the swap generated more sense of control and trust. "
              )}
              <strong>{t("La decisión final combinó ambas lógicas:", "The final decision combined both:")}</strong>{" "}
              {t(
                "sistema fluido con restricciones deliberadas. El dashboard se siente vivo, pero el usuario nunca pierde el control.",
                "a fluid system with deliberate constraints. The dashboard feels alive, but the user never loses control."
              )}
            </p>
            <p>
              {t(
                "Definido el movimiento, es hora de establecer las reglas y comenzar los diseños en baja fidelidad…",
                "With the motion defined, it was time to set the rules and start the low-fidelity designs…"
              )}
            </p>
          </div>
        </Appear>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("4.4 · Las reglas del sistema", "4.4 · The rules of the system")}</h2>
          <div className="case-prose">
            <p>
              {t(
                "Definir el comportamiento del dashboard significaba definir reglas para cada situación posible: ¿qué pasa con 1 módulo o con 8? ¿Cómo se agrega, elimina o reemplaza el contenido de un módulo? ¿Cómo comunicarle al usuario que llegó al límite?",
                "Defining the dashboard's behavior meant defining rules for every possible situation: what happens with 1 module vs. 8? How do you add, remove or replace a module's content? How do you tell the user they've hit the limit?"
              )}
            </p>
          </div>
          <CasePlaceholder
            className="case-fig--bare"
            src="assets/cases/djy-pro/collage-comportamiento"
            alt={t(
              "Diagrama de comportamiento del dashboard: secuencia de añadir módulos de 1 a 8, eliminación de módulos, vista móvil y detalle del módulo de datos de mercado.",
              "Dashboard behavior diagram: sequence of adding modules from 1 to 8, removing modules, mobile view and detail of the market data module."
            )}
            width={5204}
            height={3110}
            ratio="5204 / 3110"
            caption={t(
              "Diagrama de comportamiento, añadir módulos de 1 a 8, eliminar módulos, vista móvil y módulo de datos de mercado",
              "Behavior diagram — adding modules from 1 to 8, removing modules, mobile view and market data module"
            )}
          />

          <div className="case-prose">
            <p>
              {t(
                "La tipografía japonesa tiene reglas propias: los caracteres no pueden truncarse con la misma lógica que el español o el inglés. Si el módulo se achicaba y el texto se cortaba en el lugar equivocado, el significado podía cambiar. ",
                "Japanese typography has its own rules: characters can't be truncated with the same logic as Spanish or English. If a module shrank and text broke in the wrong place, the meaning could change. "
              )}
              <strong>
                {t(
                  "El sistema tuvo que adaptar su comportamiento — no solo su visual — según el idioma activo.",
                  "The system had to adapt its behavior — not just its visual — to the active language."
                )}
              </strong>
            </p>
          </div>

          <ResponsiveResize lang={lang} />


          <div className="case-prose">
            <p>
              {t(
                "Una vez definido el dashboard core del sistema, fue hora de seguir con los periféricos del sistema, no menos importantes, pero sí más rápidos de crear con los fundamentos de interacción ya definidos.",
                "Once the core dashboard system was defined, it was time to move on to the peripherals — no less important, but much faster to build with the interaction fundamentals already in place."
              )}
            </p>
            <div className="case-disclose">
              <details className="case-disclose-item">
                <summary>
                  <span className="case-disclose-label">
                    {t(
                      "Cómo se verían y comportarían los artículos en su propia página con sus variantes visuales y en todos los tamaños",
                      "What the articles would look like and how they'd behave on their own page, with their visual variants, across every screen size"
                    )}
                  </span>
                  <span className="case-disclose-icon" aria-hidden="true">+</span>
                </summary>
                <div className="case-disclose-panel">
                  <figure className="case-fig">
                    <div className="case-img-wrap" style={{ aspectRatio: "1536 / 1024" }}>
                      <picture>
                        <source srcSet="assets/cases/djy-pro/page-article.webp" type="image/webp" />
                        <img
                          src="assets/cases/djy-pro/page-article.png"
                          alt={t(
                            "Diseño de la página de artículo individual mostrando sus variantes visuales y comportamiento responsive en distintos tamaños de pantalla.",
                            "Article-page design showing visual variants and responsive behavior across screen sizes."
                          )}
                          width="1536"
                          height="1024"
                          loading="lazy"
                          decoding="async"
                        />
                      </picture>
                    </div>
                    <figcaption className="mono">
                      {t(
                        "Página de artículo · variantes visuales y comportamiento responsive",
                        "Article page · visual variants and responsive behavior"
                      )}
                    </figcaption>
                  </figure>
                </div>
              </details>

              <details className="case-disclose-item">
                <summary>
                  <span className="case-disclose-label">
                    {t(
                      "Cómo funcionaría el buscador, con su complejidad de condicionales AND y OR, los filtros y la funcionalidad de guardar búsquedas.",
                      "How the search would work, with its AND/OR conditionals, the filters and the saved-searches feature"
                    )}
                  </span>
                  <span className="case-disclose-icon" aria-hidden="true">+</span>
                </summary>
                <div className="case-disclose-panel">
                  <figure className="case-fig">
                    <div className="case-img-wrap" style={{ aspectRatio: "5204 / 3110" }}>
                      <picture>
                        <source srcSet="assets/cases/djy-pro/collage-searchpage.webp" type="image/webp" />
                        <img
                          src="assets/cases/djy-pro/collage-searchpage.png"
                          alt={t(
                            "Collage del buscador: campos de búsqueda con operadores AND/OR, panel de filtros, listado de resultados y la funcionalidad de guardar búsquedas como vista persistente.",
                            "Search collage: search fields with AND/OR operators, filter panel, results list and the ability to save a search as a persistent view."
                          )}
                          width="5204"
                          height="3110"
                          loading="lazy"
                          decoding="async"
                        />
                      </picture>
                    </div>
                    <figcaption className="mono">
                      {t(
                        "Buscador · condicionales AND/OR, filtros y búsquedas guardadas",
                        "Search · AND/OR conditionals, filters and saved searches"
                      )}
                    </figcaption>
                  </figure>
                </div>
              </details>

              <details className="case-disclose-item">
                <summary>
                  <span className="case-disclose-label">
                    {t(
                      "Cómo mostrar las configuraciones admitidas por sistema",
                      "How to surface the system-supported configurations"
                    )}
                  </span>
                  <span className="case-disclose-icon" aria-hidden="true">+</span>
                </summary>
                <div className="case-disclose-panel">
                  <figure className="case-fig">
                    <div className="case-img-wrap" style={{ aspectRatio: "1536 / 1024" }}>
                      <picture>
                        <source srcSet="assets/cases/djy-pro/preferences-settings.webp" type="image/webp" />
                        <img
                          src="assets/cases/djy-pro/preferences-settings.png"
                          alt={t(
                            "Pantalla de preferencias y settings del sistema mostrando las configuraciones admitidas y cómo se agrupan visualmente para el usuario.",
                            "System preferences and settings screen showing supported configurations and how they group visually for the user."
                          )}
                          width="1536"
                          height="1024"
                          loading="lazy"
                          decoding="async"
                        />
                      </picture>
                    </div>
                    <figcaption className="mono">
                      {t(
                        "Preferencias · configuraciones admitidas por sistema",
                        "Preferences · system-supported configurations"
                      )}
                    </figcaption>
                  </figure>
                </div>
              </details>
            </div>
            <p>
              {t(
                "Y claro, todos los edge cases, los empty states, las alertas y las notificaciones.",
                "And of course, every edge case, every empty state, every alert and every notification."
              )}
            </p>
          </div>
        </Appear>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("4.5 · Metodología de entrega", "4.5 · Delivery methodology")}</h2>
          <div className="case-prose">
            <p>{t("Cada semana el equipo avanzaba en capas.", "Every week the team moved forward in layers.")}</p>
            <ul className="case-arrows">
              <li>{t("Los UXAs definíamos flujos y comportamientos en baja fidelidad.", "UXAs defined flows and behaviors in low fidelity.")}</li>
              <li>{t("Los UXDs traducían eso a diseño visual.", "UXDs translated that into visual design.")}</li>
              <li>{t("Los DS designers convertían las decisiones en componentes documentados con todos sus estados y especificaciones de accesibilidad.", "DS designers turned the decisions into components, documented with all their states and accessibility specs.")}</li>
              <li>
                <strong>{t("Los devs recibían handoff incremental", "Devs got incremental handoff")}</strong>
                {t(
                  " — no un dump final, sino entregas parciales sobre las que podían avanzar en paralelo.",
                  " — not a final dump, but partial deliveries they could start building on in parallel."
                )}
              </li>
            </ul>
          </div>

          <CaseLayersDiagram lang={lang} />


          <div className="case-prose">
            <p>
              {t(
                "Cuando el sistema no tenía un componente que se necesitaba, se generaba un pedido formal al equipo de DS: ",
                "When the system didn't have a component we needed, we filed a formal request to the DS team: "
              )}
              <strong>
                {t(
                  "comportamiento completo, todos los estados, edge cases, requisitos de accesibilidad.",
                  "complete behavior, every state, edge cases, accessibility requirements."
                )}
              </strong>
            </p>
            <p>
              <em>
                {t(
                  "Siempre que se pueda, trabajar con lo que hay sin reinventar la rueda y, si se necesita algo nuevo, pedirlo bien documentado.",
                  "Whenever possible, work with what you have without reinventing the wheel — and if you need something new, ask for it well-documented."
                )}
              </em>
            </p>
          </div>
        </Appear>
      </section>

      {/* 5 — CONTEXTO (escala) — calendario + texto, flow desde 4.5 */}
      <Appear className="case-section case-section--flow shell">
        <div className="case-scale-grid">
          <figure className="case-scale-thumb">
            <picture>
              <source srcSet="assets/cases/djy-pro/calendario.webp" type="image/webp" />
              <img
                src="assets/cases/djy-pro/calendario.png"
                alt={t(
                  "Calendario de una semana típica del proyecto: bloques que representan las entregas y reuniones distribuidas entre los roles del equipo.",
                  "Calendar of a typical project week: blocks representing deliveries and meetings spread across the team's roles."
                )}
                width="1572"
                height="722"
                loading="lazy"
                decoding="async"
              />
            </picture>
            <figcaption className="mono">{t("Una semana típica del proyecto", "A typical project week")}</figcaption>
          </figure>
          <div className="case-prose">
            {lang === "en" ? (
              <p>
                Coordinating between two global media corporations, in two
                languages and remotely, was itself a design challenge.{" "}
                <em>
                  On projects this size, clarity isn't a plus — it's the
                  condition for everything else to work.{" "}
                  <strong>
                    Presenting well, documenting precisely, providing certainty
                    where there was none: that was also design.
                  </strong>
                </em>
              </p>
            ) : (
              <p>
                Coordinar entre dos corporaciones mediáticas de escala global,
                en dos idiomas y en remoto, era en sí mismo un desafío de
                diseño.{" "}
                <em>
                  En proyectos de esta escala, la claridad no es un plus — es
                  la condición para que todo lo demás funcione.{" "}
                  <strong>
                    Presentar bien, documentar con precisión, dar certeza donde
                    había incertidumbre: eso también fue diseño.
                  </strong>
                </em>
              </p>
            )}
          </div>
        </div>
        <p className="case-prose-close case-prose-close--full">
          {t("Seis meses después, el producto estaba vivo.", "Six months later, the product was live.")}
        </p>
      </Appear>

      {/* 7 — RESULTADO */}
      <Appear className="case-section shell">
        <div ref={resultRef} style={{ position: "absolute", top: 0 }} aria-hidden="true"></div>
        <div className="section-head">
          <span className="tag">{t("// RESULTADO", "// RESULT")}</span>
        </div>
        <div className="case-metrics">
          <CaseMetric value={78} suffix="%" label={t("retención de usuarios a 3 meses", "user retention at 3 months")} highlight />
          <CaseMetric value="4.2" suffix="/5" label={t("satisfacción (encuesta a 300 usuarios)", "satisfaction (300-user survey)")} />
          <CaseMetric value={6} suffix="" label={t("meses de proyecto", "months of project")} />
          <CaseMetric value={12} suffix="" label={t("personas en el equipo", "people on the team")} />
        </div>
        <div className="case-prose">
          <p>{t("Un producto nuevo, complejo, bilingüe, multicultural. Construido desde cero.", "A new product. Complex, bilingual, multicultural. Built from zero.")}</p>
        </div>
        <a className="case-link mono" href="https://yomiuri.co.jp/topics/djypro" target="_blank" rel="noreferrer">
          {t("→ Ver producto vivo: yomiuri.co.jp/topics/djypro", "→ See the live product: yomiuri.co.jp/topics/djypro")}
        </a>

        {/* Reviews del equipo (antes vivían en el panel lateral del
            desborde — ahora se renderizan inline dentro de Resultado). */}
        <div className="case-result-reviews">
          {DJY_REVIEWS.map((r, i) => (
            <blockquote key={i} className="case-natural-review">
              <p>"{djyL(r.text, lang)}"</p>
              <footer className="mono">
                — <strong>{r.name}</strong>, {djyL(r.role, lang)}
              </footer>
            </blockquote>
          ))}
        </div>
      </Appear>

      {/* 8 — REFLEXIÓN */}
      <Appear className="case-section case-section--quote shell">
        {lang === "en" ? (
          <blockquote className="case-quote mono">
            "Designing for multiple cultures isn't about adapting the visuals — it's about{" "}
            <span className="accent">questioning</span> every assumption you thought was universal."
          </blockquote>
        ) : (
          <blockquote className="case-quote mono">
            "Diseñar para múltiples culturas no es adaptar la visual — es{" "}
            <span className="accent">cuestionar</span> cada supuesto que creías universal."
          </blockquote>
        )}
        <p className="case-thanks mono" aria-label={t("Gracias por leer", "Thanks for reading")}>
          <span aria-hidden="true">❤︎</span> {t("Gracias por leer.", "Thanks for reading.")}
        </p>
      </Appear>

      {/* 9 — DESBORDE NATURAL (fallback histórico cuando el panel lateral
          no se abría). Las reviews ahora viven inline en Resultado, así que
          este bloque queda comentado. Recuperar si se revierte el flow. */}
      {/*
      {!reviewsOpened && (
        <Appear className="case-section shell">
          <div className="section-head">
            <h2>Lo que dijo el equipo</h2>
            <span className="tag">// EQUIPO</span>
          </div>
          <div className="case-natural-reviews">
            {DJY_REVIEWS.map((r, i) => (
              <blockquote key={i} className="case-natural-review">
                <p>"{r.text}"</p>
                <footer className="mono">
                  — <strong>{r.name}</strong>, {r.role}
                </footer>
              </blockquote>
            ))}
          </div>
        </Appear>
      )}
      */}

      {/* More-work bridge — 3rd level entry */}
      {onOpenMore && (
        <Appear className="case-section case-more-bridge shell">
          <div className="case-more-card">
            <span className="tag tag-line">{t("// continuación", "// continued")}</span>
            <h2 className="case-more-title mono">{t("Más trabajo con Dow Jones", "More work with Dow Jones")}</h2>
            <p className="case-prose-inline">
              {t(
                "Rediseño de Market Data Strip (MarketWatch), auditoría de artículos en 11 productos, auditoría de IA en Barron's, y WSJ Plus. Mismos principios, otros problemas.",
                "Redesigning the Market Data Strip (MarketWatch), an article-component audit across 11 products, an IA audit on Barron's, and WSJ Plus. Same principles, different problems."
              )}
            </p>
            <button className="btn btn--solid" onClick={onOpenMore}>
              {t("Ver los 4 proyectos", "See the 4 projects")} <span className="arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </Appear>
      )}

      <FooterCaseCondensed
        otherCaseLabel={t("Ver caso agente IA", "See AI agent case")}
        otherCaseHref="#caso-agente-ia"
        lang={lang}
      />
    </div>
  );
}

function CaseMetric({ value, suffix, label, highlight }) {
  const isNumeric = typeof value === "number";
  return (
    <div className={`case-metric ${highlight ? "is-highlight" : ""}`}>
      <div className="case-metric-v mono">
        {isNumeric ? <Counter target={value} format={(n) => n.toLocaleString()} /> : value}
        <span className="case-metric-suffix">{suffix}</span>
      </div>
      <div className="case-metric-l mono">{label}</div>
    </div>
  );
}

// Panel content used inside <DesbordePanel> (or as a standalone aside) for DJY reviews
function CaseDJYReviewsPanel({ onClose, lang = "es" }) {
  const [idx, setIdx] = useState(0);
  const r = DJY_REVIEWS[idx];
  const t = (es, en) => (lang === "en" ? en : es);
  return (
    <aside className="desborde-content">
      <div className="desborde-head">
        <span className="tag tag-line" style={{ color: "oklch(0.78 0.005 80)" }}>
          {t("Lo que dijo el equipo · DJY", "What the team said · DJY")}
        </span>
        <button className="desborde-x" onClick={onClose} aria-label="close">×</button>
      </div>
      <div className="desborde-body desborde-body--center">
        <div className="desborde-review-count mono">
          {String(idx + 1).padStart(2, "0")} / {String(DJY_REVIEWS.length).padStart(2, "0")}
        </div>
        <blockquote className="desborde-review-text">
          "{djyL(r.text, lang)}"
        </blockquote>
        <div className="desborde-review-who mono">
          <div className="who-name">{r.name}</div>
          <div className="who-role">{djyL(r.role, lang)}</div>
        </div>
      </div>
      <div className="desborde-foot">
        <div className="desborde-nav">
          <button
            className="btn desborde-nav-btn"
            onClick={() => setIdx((i) => (i - 1 + DJY_REVIEWS.length) % DJY_REVIEWS.length)}
          >← prev</button>
          <button
            className="btn desborde-nav-btn"
            onClick={() => setIdx((i) => (i + 1) % DJY_REVIEWS.length)}
          >next →</button>
        </div>
      </div>
    </aside>
  );
}

window.CaseDJY = CaseDJY;
window.CaseDJYReviewsPanel = CaseDJYReviewsPanel;
window.DJY_REVIEWS = DJY_REVIEWS;
window.CasePlaceholder = CasePlaceholder;
